import React, { useState } from "react";
import { Alert, Button, Card, Input } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import type { ActionPlanAiPendingPlan, ActionPlanAiPendingState } from "@/types/action-plan-v2/ActionPlanAiPlan";
import cardStyles from "./ActionPlanPendingCard.module.css";

type Props = {
  pending: ActionPlanAiPendingState;
  onConfirm: (plan: ActionPlanAiPendingPlan) => void;
  onDismiss: () => void;
};

/** Trường chữ chính của mục — sửa được trên thẻ trước khi duyệt. */
const TEXT_KEYS = ["text", "name", "result"] as const;

/** Thẻ duyệt lệnh ghi dữ liệu B1–B5 (plan bsc_* từ trợ lý): bước, nhóm, trước → sau; Duyệt thì engine ghi. */
export const BscStepPendingCard: React.FC<Props> = ({ pending, onConfirm, onDismiss }) => {
  const { plan, status, error } = pending;
  const op = plan.action.replace("bsc_", "");
  const isDelete = op === "delete";
  const textKey = TEXT_KEYS.find((k) => typeof plan.fields?.[k] === "string");
  const [draft, setDraft] = useState<string>(textKey ? String(plan.fields?.[textKey] ?? "") : "");
  const busy = status === "confirming";
  const edited = textKey !== undefined && draft.trim() !== String(plan.fields?.[textKey] ?? "").trim();

  const confirm = () => {
    if (!edited || !textKey) {
      onConfirm(plan);
      return;
    }
    onConfirm({ ...plan, fields: { ...plan.fields, [textKey]: draft.trim() }, afterText: draft.trim() });
  };

  return (
    <Card
      size="small"
      className={cardStyles.card}
      title={
        <span className={cardStyles.headTitle}>
          <span className={`${cardStyles.headIcon} ${isDelete ? cardStyles.headIconDanger : ""}`} aria-hidden>
            {isDelete ? <DeleteOutlined /> : op === "update" ? <EditOutlined /> : <PlusOutlined />}
          </span>
          {plan.label || plan.stepTitle || "Dữ liệu bước"}
        </span>
      }
    >
      <div className={cardStyles.scope}>
        {plan.stepTitle}
        {plan.sectionLabel ? ` · ${plan.sectionLabel}` : ""}
      </div>
      {plan.beforeText ? (
        <div className="text-[12px] text-[#667085]">
          <span className="font-medium">Hiện tại: </span>
          <span className={isDelete ? "line-through" : ""}>{plan.beforeText}</span>
        </div>
      ) : null}
      {!isDelete && textKey && status !== "done" ? (
        <Input.TextArea
          className="!mt-2"
          autoSize={{ minRows: 1, maxRows: 6 }}
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Nội dung sẽ lưu"
        />
      ) : !isDelete && plan.afterText ? (
        <div className="!mt-2 text-[13px] font-medium">{plan.afterText}</div>
      ) : null}
      {isDelete && status !== "done" ? (
        <Alert type="warning" showIcon className="!mt-2 !text-xs" message="Mục sẽ bị xoá khỏi trang bước, không hoàn tác được." />
      ) : null}
      {status === "error" && error ? <Alert type="error" showIcon message={error} className="!mt-2 !text-xs" /> : null}
      {status === "done" ? (
        <Alert type="success" showIcon className="!mt-2 !text-xs" message={isDelete ? "Đã xoá." : "Đã lưu vào trang bước."} />
      ) : null}
      {status === "pending" || busy ? (
        <div className={cardStyles.actions}>
          <Button size="small" type="text" onClick={onDismiss} disabled={busy}>
            Bỏ qua
          </Button>
          <Button
            type="primary"
            danger={isDelete}
            size="small"
            loading={busy}
            disabled={!isDelete && textKey !== undefined && !draft.trim()}
            onClick={confirm}
          >
            {isDelete ? "Xoá" : op === "update" ? "Lưu thay đổi" : "Thêm"}
          </Button>
        </div>
      ) : null}
    </Card>
  );
};
